import React, { useEffect, useRef } from 'react';

/**
 * NukeButtonAnimation - Plays country-specific "button press" video before nuclear strike
 * Shows dramatic 5-second clip of leader pressing the nuclear button
 */
export default function NukeButtonAnimation({ sourceCountry, onComplete }) {
  const videoRef = useRef(null);
  const audioRefs = useRef({ 
    alarm: null, 
    music: null, 
    drama: null, 
    press: null, 
    silo: null 
  }); // Track all audio sources

  useEffect(() => {
    // Map country codes to button video files
    const buttonVideos = {
      'USA': '/assets/nuke-buttons/usa-nuke-button.mp4',
      'RUS': '/assets/nuke-buttons/rus-nuke-button.mp4',
      'CHN': '/assets/nuke-buttons/chn-nuke-button.mp4',
      'EU': '/assets/nuke-buttons/eu-nuke-button.mp4',
      'NK': '/assets/nuke-buttons/nk-nuke-button.mp4'
    };

    const videoPath = buttonVideos[sourceCountry];
    
    if (!videoPath) {
      console.warn(`No nuke button video for ${sourceCountry}, skipping`);
      onComplete();
      return;
    }

    console.log(`🔴 NUKE BUTTON: ${sourceCountry} pressing the button...`);

    // Play alarm sound at 20% volume (background)
    const alarmSound = new Audio('/assets/audio/air-raid-siren-3.mp3');
    alarmSound.volume = 0.2; // ⭐ Reduced to 20% volume
    alarmSound.play().catch(e => console.log('Alarm sound failed:', e));
    audioRefs.current.alarm = alarmSound;

    // Play new imperial nuke soundtrack at full volume
    const nukeMusic = new Audio('/assets/audio/nuke-song-imperial.mp3');
    nukeMusic.volume = 1.0; // ⭐ Full volume for dramatic music
    nukeMusic.currentTime = 0.5; // ✅ Skip first 0.5 seconds (intro/silence)
    nukeMusic.preload = 'auto'; // ✅ Preload to reduce loading delay
    nukeMusic.play().catch(e => console.log('Nuke music failed:', e));
    audioRefs.current.music = nukeMusic;

    // 🔊 NEW: Layer additional dramatic sound effects (quieter than main soundtrack)
    // Drama layer - intense orchestral buildup
    const dramaSound = new Audio('/assets/audio/nuke-button-drama.mp3');
    dramaSound.volume = 0.6; // 60% volume - subordinate to main soundtrack
    dramaSound.preload = 'auto';
    dramaSound.play().catch(e => console.log('Drama sound failed:', e));
    audioRefs.current.drama = dramaSound;

    // Button press - sharp mechanical click
    const pressSound = new Audio('/assets/audio/nuke-button-press.mp3');
    pressSound.volume = 0.7; // 70% volume - subordinate to main soundtrack
    pressSound.preload = 'auto';
    pressSound.play().catch(e => console.log('Press sound failed:', e));
    audioRefs.current.press = pressSound;

    // Silo opening - mechanical rumble
    const siloSound = new Audio('/assets/audio/nuke-silo-open-mechanical.mp3');
    siloSound.volume = 0.5; // 50% volume - background layer
    siloSound.preload = 'auto';
    siloSound.play().catch(e => console.log('Silo sound failed:', e));
    audioRefs.current.silo = siloSound;

    // Auto-play the video
    if (videoRef.current) {
      videoRef.current.play().catch(e => console.log('Video autoplay failed:', e));
    }

    // Cleanup - stop all audio tracks
    return () => {
      if (audioRefs.current.alarm) {
        audioRefs.current.alarm.pause();
        audioRefs.current.alarm.currentTime = 0;
      }
      if (audioRefs.current.music) {
        audioRefs.current.music.pause();
        audioRefs.current.music.currentTime = 0;
      }
      if (audioRefs.current.drama) {
        audioRefs.current.drama.pause();
        audioRefs.current.drama.currentTime = 0;
      }
      if (audioRefs.current.press) {
        audioRefs.current.press.pause();
        audioRefs.current.press.currentTime = 0;
      }
      if (audioRefs.current.silo) {
        audioRefs.current.silo.pause();
        audioRefs.current.silo.currentTime = 0;
      }
    };
  }, [sourceCountry, onComplete]);

  // Handle video end
  const handleVideoEnd = () => {
    console.log('🔴 Nuke button video complete, launching missile...');
    // Stop all audio tracks
    if (audioRefs.current.alarm) {
      audioRefs.current.alarm.pause();
    }
    if (audioRefs.current.music) {
      audioRefs.current.music.pause();
    }
    if (audioRefs.current.drama) {
      audioRefs.current.drama.pause();
    }
    if (audioRefs.current.press) {
      audioRefs.current.press.pause();
    }
    if (audioRefs.current.silo) {
      audioRefs.current.silo.pause();
    }
    onComplete();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black">
      {/* Full-screen video of button press */}
      <video
        ref={videoRef}
        className="w-full h-full object-cover"
        onEnded={handleVideoEnd}
        playsInline
        muted={false}
      >
        <source 
          src={`/assets/nuke-buttons/${sourceCountry.toLowerCase()}-nuke-button.mp4`} 
          type="video/mp4" 
        />
      </video>

      {/* "LAUNCHING..." overlay text */}
      <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2">
        <div className="bg-red-600/90 px-8 py-4 rounded-lg border-2 border-red-300 animate-pulse">
          <p className="text-white text-3xl font-bold tracking-wider">
            ☢️ LAUNCHING NUCLEAR STRIKE ☢️
          </p>
        </div>
      </div>

      {/* Country name top overlay */}
      <div className="absolute top-10 left-1/2 transform -translate-x-1/2">
        <div className="bg-black/80 px-6 py-3 rounded-lg border border-red-500">
          <p className="text-red-400 text-xl font-bold uppercase tracking-widest">
            {sourceCountry} - NUCLEAR AUTHORIZATION
          </p>
        </div>
      </div>
    </div>
  );
}
